// apps/web/src/lib/showcase/api-client.ts
import { showcaseService } from "@acme/api";
import { createServerFn } from "@tanstack/react-start";
import { array, number, object, optional, string } from "valibot";
import { emit } from "~/lib/showcase/realtime";
import { getOrCreateShowcaseSession } from "~/lib/showcase/session";

export const searchRoomsFn = createServerFn({ method: "POST" })
  .validator(
    object({ checkIn: string(), checkOut: string(), guests: number() })
  )
  .handler(async (ctx) => {
    const sessionId = await getOrCreateShowcaseSession();
    return showcaseService.searchRooms(ctx.data, sessionId);
  });

export const listAddonsFn = createServerFn({ method: "GET" }).handler(() =>
  showcaseService.listAddons()
);

export const listFaqFn = createServerFn({ method: "GET" }).handler(() =>
  showcaseService.listFaq()
);

export const applyPromoFn = createServerFn({ method: "POST" })
  .validator(object({ code: string(), nights: number(), subtotal: number() }))
  .handler((ctx) =>
    showcaseService.applyPromo(
      ctx.data.code,
      ctx.data.subtotal,
      ctx.data.nights
    )
  );

export const createBookingFn = createServerFn({ method: "POST" })
  .validator(
    object({
      addonIds: array(string()),
      checkIn: string(),
      checkOut: string(),
      guestName: optional(string()),
      guests: number(),
      promoCode: optional(string()),
      roomId: string(),
    })
  )
  .handler(async (ctx) => {
    const sessionId = await getOrCreateShowcaseSession();
    const result = await showcaseService.createBooking({
      ...ctx.data,
      sessionId,
    });
    emit(sessionId, {
      bookingId: result.id,
      checkIn: ctx.data.checkIn,
      checkOut: ctx.data.checkOut,
      roomId: ctx.data.roomId,
      type: "booking_created",
    });
    return result;
  });

export const cancelBookingFn = createServerFn({ method: "POST" })
  .validator(object({ bookingId: string() }))
  .handler(async (ctx) => {
    const sessionId = await getOrCreateShowcaseSession();
    await showcaseService.cancelBooking(sessionId, ctx.data.bookingId);
    emit(sessionId, {
      bookingId: ctx.data.bookingId,
      type: "booking_cancelled",
    });
    return { ok: true as const };
  });

export const updateBookingFn = createServerFn({ method: "POST" })
  .validator(
    object({
      bookingId: string(),
      channel: optional(string()),
      checkIn: optional(string()),
      checkOut: optional(string()),
      guestName: optional(string()),
      guests: optional(number()),
    })
  )
  .handler(async (ctx) => {
    const sessionId = await getOrCreateShowcaseSession();
    try {
      await showcaseService.updateBooking({
        bookingId: ctx.data.bookingId,
        channel: ctx.data.channel,
        checkIn: ctx.data.checkIn,
        checkOut: ctx.data.checkOut,
        guestName: ctx.data.guestName,
        guests: ctx.data.guests,
        sessionId,
      });
    } catch (err) {
      if (err instanceof showcaseService.UpdateBookingError) {
        return { code: err.code, message: err.message, ok: false as const };
      }
      throw err;
    }
    emit(sessionId, {
      bookingId: ctx.data.bookingId,
      type: "booking_updated",
    });
    return { ok: true as const };
  });

export const listBookingsFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const sessionId = await getOrCreateShowcaseSession();
    return showcaseService.listBookings(sessionId);
  }
);

export const getBookingFn = createServerFn({ method: "GET" })
  .validator(object({ bookingId: string() }))
  .handler(async (ctx) => {
    const sessionId = await getOrCreateShowcaseSession();
    return showcaseService.getBooking(sessionId, ctx.data.bookingId);
  });
